import {
  type ApolloError,
  type BaseMutationOptions,
  useApolloClient,
} from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { log } from 'src/modules/error-logger';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../../data';
import {
  updateLocationsFailure,
  updateLocationsSuccess,
} from '../actionCreators';

import translations from './.vocab';
import type { EditedLocation } from './EditPreferredLocation/EditPreferredLocation';
import {
  updateCacheUpdatePreferredLocations,
  generateUpdatePreferredLocationsOptimisticResponse,
} from './apolloCache';
import { useUpdatePreferredLocationsAnalytics } from './useUpdatePreferredLocationsAnalytics';

export const usePreferredLocations = () => {
  const { zone, language } = useAppConfig();
  const { data, loading, error } = useQuery(
    Schema.GetPreferredLocations2Document,
    {
      variables: {
        zone,
        languageCode: language,
      },
    },
  );

  const preferredLocations =
    loading || error ? [] : data?.viewer?.preferredLocations2 || [];

  return { preferredLocations, loading, error };
};

const key = 'next-role-preferred-locations';

export const useUpdatePreferredLocations = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const { candidateId } = useCandidateId();
  const apolloClient = useApolloClient();

  const { updatePreferredLocationsAnalytics } =
    useUpdatePreferredLocationsAnalytics();

  const onMutate = (
    _variables: Schema.UpdatePreferredLocations2MutationVariables,
    mutationMetadata?: {
      newLocations: Schema.Location2[];
      locationTrackingList?: EditedLocation[];
    },
  ) => {
    if (mutationMetadata?.locationTrackingList?.length) {
      updatePreferredLocationsAnalytics(mutationMetadata.locationTrackingList);
    }
  };
  const onCompleted = () => {
    dispatch(updateLocationsSuccess());
  };
  const onError = (error: ApolloError, clientOptions?: BaseMutationOptions) => {
    log(error, {
      kind: 'GraphMutationError',
      input: clientOptions,
    });
    dispatch(updateLocationsFailure(error));
  };

  const [updatePreferredLocations] = useToastMutation(
    Schema.UpdatePreferredLocations2Document,
    {
      key,
      errorActionRouteParameters: {
        mode: 'EDIT',
        to: 'preferred-locations',
        params: { focusOn: 'preferred-locations' },
      },
      onMutate,
      onCompleted,
      onError,
      successToastMessage: t('Preferred locations updated'),
      update: updateCacheUpdatePreferredLocations,
      optimisticResponse: generateUpdatePreferredLocationsOptimisticResponse({
        _id: candidateId || '',
        apolloClient,
      }),
    },
  );

  return {
    updatePreferredLocations,
  };
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetPreferredLocationsRequest = (
  preferredLocations2: Schema.Location2[] = [],
) => ({
  request: {
    query: Schema.GetPreferredLocations2Document,
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        preferredLocations2,
      },
    },
  },
});
